import React from "react";
import {
  Code,
  HStack,
  Heading,
  ListItem,
  Select,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";

import { Module } from "@constants";
import { DbSearch, Loading, ModuleGrid, PageSwitcher } from "@components";
import { DEFAULT_COLLECTIONS_HOOK_RETURN, GetCollectionResp } from "@hooks";
import { Layout } from "./PageLayouts";

const SEARCH_HELP_TEXT = (
  <VStack gap="2px" alignItems="stretch">
    <Text>Use this search bar to filter the overall list of modules.</Text>
    <Text>
      For example, you can search for the modules that Jane Doe worked on with
      any of the following queries:
    </Text>
    <UnorderedList paddingLeft="16px">
      <ListItem>
        <Code>{"'Jane Doe'"}</Code>
      </ListItem>
      <ListItem>
        <Code>{"contributor:'Jane Doe'"}</Code>
      </ListItem>
      <ListItem>
        <Code>{"contibutor:doe"}</Code>
      </ListItem>
    </UnorderedList>
    <Text>
      Use the <Code>key:value</Code> syntax to search for any of the following
      fields:
    </Text>
    <UnorderedList paddingLeft="16px">
      <ListItem>
        <Code>id</Code> (serial number for the module itself)
      </ListItem>
      <ListItem>
        <Code>module</Code> (the variety of module, 2S or PS)
      </ListItem>
      <ListItem>
        <Code>part</Code> (the name for the part at it&lquo;s current point in
        production, eg <Code>MaPSA</Code>)
      </ListItem>
      <ListItem>
        <Code>component</Code> (the part category of the module at the current
        point of production, eg <Code>sensor</Code>, <Code>hybrid</Code>,{" "}
        <Code>module</Code>)
      </ListItem>
      <ListItem>
        <Code>location</Code> (current location of the module, or
        &ldquo;courier&rdquo; for modules in transit)
      </ListItem>
      <ListItem>
        <Code>status</Code> (current status of the module)
      </ListItem>
      <ListItem>
        <Code>contributor</Code> (anyone who has contributed to the produciton
        of a module)
      </ListItem>
      <ListItem>
        <Code>step</Code> (database name of specific production steps)
      </ListItem>
      <ListItem>
        <Code>image</Code> (titles of uploaded images)
      </ListItem>
      <ListItem>
        <Code>serial</Code> (serial numbers for related components)
      </ListItem>
    </UnorderedList>
    <Text>
      All queries are disjunctive: modules are returned if any of the submitted
      search terms apply to them.
    </Text>
  </VStack>
);

export function ModulesPage() {
  const [searchModules, setSearchModules] = React.useState<
    GetCollectionResp<Module>
  >(DEFAULT_COLLECTIONS_HOOK_RETURN);
  const [size, setPageSize] = React.useState(24);
  const [page, setPage] = React.useState(1);
  const paginationOpts = React.useMemo(() => ({ size, page }), [size, page]);
  const totalPages = React.useMemo(
    () =>
      searchModules.count === undefined
        ? undefined
        : Math.ceil(searchModules.count / size),
    [searchModules],
  );

  return (
    <Layout>
      <Heading>Modules</Heading>
      <DbSearch
        marginTop="8"
        marginBottom="8"
        collection="modules"
        resultsCallback={setSearchModules}
        helpText={SEARCH_HELP_TEXT}
        paginationOpts={paginationOpts}
      />
      {searchModules.documents === undefined ? <Loading /> : undefined}
      {searchModules.count !== undefined && searchModules.count === 0 ? (
        <Text>No documents were returned from your search.</Text>
      ) : undefined}
      {searchModules.documents && searchModules.documents.length !== 0 ? (
        <>
          <PageSwitcher page={page} setPage={setPage} totalPages={totalPages} />
          <ModuleGrid modules={searchModules.documents} />
          <PageSwitcher page={page} setPage={setPage} totalPages={totalPages} />
          <HStack alignItems="baseline" justifyContent="flex-end">
            <Text>Page Size</Text>
            <Select
              maxWidth={200 / 16 + "rem"}
              onChange={({ target: { value } }) => {
                const newPageSize = Number(value);
                if (!isNaN(newPageSize)) setPageSize(newPageSize);
              }}
            >
              <option disabled>~~Please Select One~~</option>
              <option value={12}>12</option>
              <option value={24}>24</option>
              <option value={36}>36</option>
              <option value={48}>48</option>
            </Select>
          </HStack>
        </>
      ) : undefined}
    </Layout>
  );
}
